/**
 * Progress. Recurring errors are a query, not a guess — that is what the
 * closed tag set buys (docs/03).
 */

import { api, ApiError } from "../api.ts";
import type { ProgressResponse } from "../types.ts";
import { bar, el, mount, percent, primaryButton, ptSpan, ruleTil, shell, statTile } from "../ui.ts";

export async function renderProgress(back: () => void): Promise<void> {
	let data: ProgressResponse;
	try {
		data = await api.progress();
	} catch (error) {
		mount(
			shell({
				back: { label: "Hoje", onClick: back },
			eyebrowLeft: "Progresso",
				body: [
					el("div", {
						class: "notice",
						text: error instanceof ApiError ? error.message : "Progress needs a connection.",
					}),
				],
				footer: primaryButton("Back to today", back, { variant: "ghost" }),
			}),
		);
		return;
	}

	const body: Node[] = [];

	if (data.concepts.length > 0) {
		body.push(el("div", { class: "meta", text: "Concept confidence" }));
		for (const concept of data.concepts.slice(0, 10)) {
			body.push(
				el(
					"div",
					{ style: "margin-top:13px" },
					el(
						"div",
						{ style: "display:flex;justify-content:space-between;align-items:baseline;gap:12px" },
						ptSpan(concept.label_pt || concept.slug, "pt"),
						el("span", { class: "en", style: "font-size:12px", text: percent(concept.confidence) }),
					),
					bar(
						concept.confidence,
						concept.confidence >= 0.85 ? "var(--jade)" : concept.confidence >= 0.7 ? "var(--azul)" : "var(--ochre)",
					),
				),
			);
		}
	} else {
		body.push(el("p", { class: "en", text: "No concepts measured yet. They appear once you have studied a few sessions." }));
	}

	if (data.recurring_errors.length > 0) {
		body.push(ruleTil());
		body.push(el("div", { class: "meta", text: "Recurring · last 30 days" }));
		for (const error of data.recurring_errors.slice(0, 6)) {
			body.push(
				el(
					"div",
					{ class: "card", style: "margin-top:11px;padding:13px 15px" },
					el(
						"div",
						{ style: "display:flex;justify-content:space-between;align-items:baseline;gap:12px" },
						el("span", { class: "en en--ink", style: "font-weight:500", text: error.tag.replace(/_/g, " ") }),
						el("span", { class: error.count >= 5 ? "chip chip--brick" : "chip chip--ochre", text: `${error.count}×` }),
					),
					el(
						"p",
						{ class: "pt", style: "font-size:16px;margin-top:8px", lang: "pt-BR" },
						el("span", { class: "diff__sub", text: error.example.learner }),
						"  →  ",
						el("span", { style: "color:var(--jade)", text: error.example.corrected }),
					),
				),
			);
		}
	}

	body.push(
		el(
			"div",
			{ class: "grid-2 stack--tight" },
			statTile("Known", String(data.items.known)),
			statTile("Learning", String(data.items.learning)),
			statTile("Due now", String(data.items.due_now)),
			statTile("Sessions", String(data.sessions_completed)),
		),
	);

	if (data.history.length > 0) {
		body.push(ruleTil());
		body.push(el("div", { class: "meta", text: "Recent sessions" }));
		for (const entry of data.history.slice(0, 8)) {
			body.push(
				el(
					"div",
					{ class: "card", style: "margin-top:9px;padding:13px 15px" },
					el(
						"div",
						{ style: "display:flex;justify-content:space-between;align-items:baseline;gap:12px" },
						ptSpan(entry.title_pt, "pt"),
						el("span", {
							class: "en",
							style: "font-size:12px",
							text: entry.accuracy === null ? entry.state : percent(entry.accuracy),
						}),
					),
					entry.note === null || entry.note === ""
						? null
						: el("p", { class: "en", style: "margin-top:6px;font-size:12.5px", lang: "en", text: entry.note }),
				),
			);
		}
	}

	mount(
		shell({
			back: { label: "Hoje", onClick: back },
			eyebrowLeft: "Progresso",
			eyebrowRight: `${data.sessions_completed} sessões`,
			body,
			footer: primaryButton("Back to today", back, { variant: "ghost" }),
		}),
	);
}
